import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { createEditor, Transforms, Editor, Path, Node, Text, Range } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { debounce } from 'lodash';
import { el } from 'redom';
import { useStore } from '@nanostores/react';

import * as basicEditorValueSerializer from 'basic-editor-value-serializer';

import application from 'application';

import { seriesSync } from 'utilities';

import editorHandles from 'constants/editor-handles';

import { hasSpinner, getSelectionRectangle, elementsToSelfContainedHTML } from './utilities';

import plugins from './plugins';
import withGroupedHistory from './plugins/grouped-history';

import Element, { Leaf } from './elements';
import types from './elements/types';
import { DEFAULT_MENTION_TYPES } from './elements/mention/mention-types';
import trackGalleryResources from './gallery-resource-tracker';
import trackCarouselResources from './carousel-resource-tracker';

import EditorActions from './editor-options';
import DragHandleMenu from './drag-handle-menu';
import MoreNodeActionsMenu from './more-node-actions-menu/react';
import Toolbar from './toolbar';
import HelptapEditor from './helpers';
import { /*nodeMenu, keyFromPath, openNodeMenu, closeNodeMenu, nodeRootByKey*/ ensureEditorStores, disposeEditorStores, SYM_STORES, SYM_OPEN, SYM_CLOSE, keyFromPath } from './stores/active-block';

import './index.scss';

const OPEN_MENU = Symbol.for('vz.openNodeMenu'); // shared key across files
const CLOSE_MENU = Symbol.for('vz.closeNodeMenu');

const normalize = basicEditorValueSerializer.normalizers.normalize;

let toolbarCount = 0;

function useStableEditor() {
  const ref = useRef(null);
  if (!ref.current) ref.current = withHistory(withReact(createEditor()));
  return ref.current;
}

const BasicTextEditor = React.memo((props) => {
  const enableAdvancedOptions = determineAdvancedOptionsEnabled();
  const disableAdvancedDragHandleFeatures = determineAdvancedDragHandleFeatures();
  const enabledDragAndDropOnly = props.enabledDragAndDropOnly;

  const editorMainReference = useRef(null);
  const renderEditorLeaf = useCallback(_renderEditorLeaf, []);

  const viewportReference = document.getElementById('startup-main');

  const [slateKey, setSlateKey] = useState(0);
  const [value, setValue] = useState(() => normalize(props.value));
  const [rootState, setRootState] = useState({
    readOnly: props.readOnly,
    nodeMenuDisplayDetails: {
      display: true,
      anchorRectangle: null,
      nodeElement: null,
      elementPath: [0],
      customNodeControls: [],
      disabledNodeControls: []
    },
    moreNodeActionsMenuDisplayDetails: {
      display: false,
      nodePath: null,
      anchor: null,
      element: null
    },
    activeNodePathReference: getDefaultActiveNodePath(),
    activeHandle: null
  });

  const [editorMenuDisplayDetails, setEditorMenuDisplayDetails] = useState({
    display: false,
    anchorRectangle: null,
    caller: null,
    start: null,
    end: null,
    searchTerm: '',
    actionIndex: 0,
    applySelectedAction: false
  });
  const editorRef = useRef(null);
  if (editorRef.current === null) {

    editorRef.current = (function initializeEditor() {

      const appliedPlugins = [withReact, withHistory, ...plugins];

      function withSelectionChangeEmitter(editorInstance) {
        const originalApply = editorInstance.apply;
        editorInstance.apply = function applyWithSelectionTracking(operation) {
          const previousSelection = editorInstance.selection;
          originalApply(operation);
          if (JSON.stringify(editorInstance.selection) !== JSON.stringify(previousSelection)) {
            queueMicrotask(function notifySelectionChange() {
              if (selectionEmitterRef.current !== null) {
                selectionEmitterRef.current();
              }
            });
          }
        };
        return editorInstance;
      }

      return seriesSync(createEditor(), [...appliedPlugins, withSelectionChangeEmitter, withGroupedHistory, (editor) => {
        editor.enabledElements = props.enabledElements || [];
        editor.disabledElements = props.disabledElements || [];
        editor.parentEntityId = props.parentEntityId;
        editor.parentEntityType = props.parentEntityType;
        editor.mentionTypes = props.mentionTypes || DEFAULT_MENTION_TYPES;
        return editor;
      }]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })([]);
  }
  const editor = editorRef.current;
  const previousSelectionSignatureRef = useRef(null);
  const selectionEmitterRef = useRef(null);

  const [aiSelectionHighlightRange, setAiSelectionHighlightRange] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const decorateEditorRanges = useCallback(_decorateEditorRanges, [aiSelectionHighlightRange]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderEditorElement = useCallback(_renderEditorElement, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTryCallOnChange = useCallback(debounce(function debounced(...parameters) {
    tryCallOnChange(...parameters);
  }, props.onChangeDelay || 500), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editorChangeHandler = useCallback(getEditorChangeHandler(), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollingContainerDragOverHandler = useCallback(debounce(handleScrollingContainerDragOver, 10), []);

  useEffect(() => {
    ensureEditorStores(editor);
    return () => disposeEditorStores(editor); // <- cleanup on unmount
  }, [editor]);

  const { nodeMenu, openNodeMenu, closeNodeMenu, nodeRootByKey } = ensureEditorStores(editor);

  const nodeMenuState = useStore(nodeMenu);

  const _openNodeMenu = useCallback((opts) => {
    const key = keyFromPath(opts.getPath());
    editor[SYM_OPEN]({
      key,
      getPath: opts.getPath,
      anchorRect: opts.anchorRectangle, // plain object
      nodeRoot: opts.nodeRoot || null, // DOM node kept in Map, not store
      customNodeControls: opts.customNodeControls || [],
      disabledNodeControls: opts.disabledNodeControls || []
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _closeNodeMenu = useCallback(() => { editor[SYM_CLOSE](); }, []);

  if (!editor[OPEN_MENU]) editor[OPEN_MENU] = _openNodeMenu;
  if (!editor[CLOSE_MENU]) editor[CLOSE_MENU] = _closeNodeMenu;

  useEffect(function onFocusChange() {

    tryFocusEditor();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focus]);

  // useEffect(() => {
  //   const next = normalize(props.value);
  //   Transforms.deselect(editor);
  //   Editor.withoutNormalizing(editor, () => {
  //     setValue(next);
  //   });
  //   // If your incoming doc differs structurally (often true), force a remount:
  //   setSlateKey(k => k + 1);
  // }, [props.value]);

  useEffect(function onLoad() {

    toolbarCount++;

    tryFocusEditor();

    return function onUnload() {
      toolbarCount--;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onLoad() {

    if (typeof props.onEditorReady !== 'function') {
      return;
    }

    props.onEditorReady(editor);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onLoad() {

    tryLoadFontFamilies(props.value);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function tryShowCommandMenuOnLoad() {

    if (props.commandMenuOnByDefault !== true) {
      return;
    }

    var timeoutId = setTimeout(function showMenuAfterRender() {

      ReactEditor.focus(editor);

      var firstElementPath = [0, 0];
      Transforms.select(editor, { path: firstElementPath, offset: 0 });
      Transforms.insertText(editor, '/');

      showEditorMenu();

    }, 150);

    return function cancelShowMenu() {

      clearTimeout(timeoutId);

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function tryRegisterScrollHandler() {

    if (typeof props.scrollingContainerSelector !== 'string') {
      return;
    }

    const scrollingContainer = document.querySelector(props.scrollingContainerSelector);

    /* beautify ignore:start */
    scrollingContainer?.addEventListener('dragover', scrollingContainerDragOverHandler);
    /* beautify ignore:end */

    return function unregisterScrollHandler() {

      /* beautify ignore:start */
      scrollingContainer?.removeEventListener('dragover', scrollingContainerDragOverHandler);
      /* beautify ignore:end */

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onReadOnlyChange() {

    setReadOnly(props.readOnly);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.readOnly]);

  function handleScrollingContainerDragOver(event) {

    const container = document.querySelector(props.scrollingContainerSelector);
    var editor = editorMainReference.current;
    var scrollSpeed = 10; // Adjust the scroll speed as needed
    var scrollThreshold = 60; // Distance from the edge to start scrolling

    var containerRect = container.getBoundingClientRect();
    var mouseY = event.clientY;
    var middleY = containerRect.top + (containerRect.height / 2);

    if (mouseY < middleY - scrollThreshold) {
      // Scroll up only if not already at the top
      if (editor.scrollTop > 0) {
        editor.scrollTop -= scrollSpeed;
      }
    } else if (mouseY > middleY + scrollThreshold) {
      // Scroll down only if not already at the bottom
      if (editor.scrollTop < editor.scrollHeight - editor.clientHeight) {
        editor.scrollTop += scrollSpeed;
      }
    }

  }

  function determineAdvancedDragHandleFeatures() {

    if (typeof props.disableAdvancedDragHandleFeatures === 'boolean') {
      return props.disableAdvancedDragHandleFeatures;
    }

    return true;

  }

  function determineAdvancedOptionsEnabled() {

    return props.enableAdvancedOptions === true && props.readOnly !== true;

  }

  function getEditorChangeHandler() {

    return handleEditorContentChange;

  }

  function getDefaultActiveNodePath() {

    const activeNodePathReference = { current: [] };

    return activeNodePathReference;

  }

  function setReadOnly(readOnly) {

    setRootState({
      ...rootState,
      readOnly
    });

  }

  function tryFocusEditor() {

    if (typeof props.focus === 'undefined') {
      return;
    }

    ReactEditor.focus(editor);

    tryCallPositionAdjuster();

  }

  function tryCallPositionAdjuster() {

    if (typeof props.adjustInputPosition !== 'function') {
      return;
    }

    return props.adjustInputPosition();

  }

  function toggleEditorMenu(options) {

    HelptapEditor.moveCursorToStartOfTheLine(editor, options.elementPath);

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails(editorMenuDisplayDetails) {

      return {
        display: editorMenuDisplayDetails.display === false,
        ...options,
        start: null,
        end: null,
        searchTerm: '',
        actionIndex: 0,
        applySelectedAction: false
      };

    });

  }

  function showEditorMenu() {

    let anchorRectangle = null;

    if (editor.selection === null) {
      return;
    }

    if (editorMenuDisplayDetails.display === true) {
      return;
    }

    const ancestors = Path.ancestors(editor.selection.anchor.path);

    const [node] = Editor.node(editor, ancestors[1]);

    if (node.type === types.TABLE) {
      return;
    }

    anchorRectangle = getSelectionRectangle();

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails() {
      return {
        display: true,
        anchorRectangle,
        caller: EditorActions.callers.KEYBOARD,
        start: editor.selection.anchor,
        end: null,
        searchTerm: '',
        actionIndex: 0,
        applySelectedAction: false
      };
    });

  }

  function tryCallOnFocusCallabck(...parameters) {

    const onFocus = props.onFocus;

    if (typeof onFocus !== 'function') {
      return;
    }

    onFocus(...parameters);

  }

  function tryCallOnBlurCallabck(...parameters) {

    const onBlur = props.onBlur;

    if (typeof onBlur !== 'function') {
      return;
    }

    onBlur(...parameters);

  }

  function tryShowEditorMenu() {

    const selection = editor.selection;

    if (selection === null) {
      return;
    }

    const focus = selection.focus;

    if (focus.offset !== 0) {

      const previousCharacter = HelptapEditor.getPreviousCharacter(editor);

      if (previousCharacter === '/') {
        hideEditorMenu();
        return;
      }

    }

    showEditorMenu();

  }

  function hideNodeMenu() {

    let anchorRectangle = null;

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        nodeMenuDisplayDetails: {
          display: false,
          anchorRectangle
        },
        activeNodePathReference: getDefaultActiveNodePath()
      };

    });

  }

  function tryFilterEditorMenuItems() {

    const start = editorMenuDisplayDetails.start;

    if (start === null) {
      return;
    }

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails(editorMenuDisplayDetails) {

      return {
        ...editorMenuDisplayDetails,
        end: editor.selection.focus
      };
    });

  }

  function hideEditorMenu() {

    let anchorRectangle = null;

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails(editorMenuDisplayDetails) {
      return {
        ...editorMenuDisplayDetails,
        display: false,
        anchorRectangle,
        start: null,
        end: null,
        searchTerm: ''
      };
    });

  }

  function convertToOrCreateCodeBlock() {

    const [match] = Editor.nodes(editor, {
      match(node) {
        return node.type === 'code';
      }
    });

    let nodeType = 'code';

    if (typeof match !== 'undefined') {
      nodeType = 'paragraph';
    }

    Transforms.setNodes(editor, { type: nodeType }, {
      match(node) {
        return Editor.isBlock(editor, node);
      }
    });

  }

  function isCommandKeyPressed(event) {

    return event.metaKey || event.ctrlKey;

  }

  function tryHandleDownArrowKeyEventForSlashMenu(event) {

    if (editorMenuDisplayDetails.display === false) {
      return;
    }

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails(editorMenuDisplayDetails) {

      let updated = editorMenuDisplayDetails.actionIndex + 1;

      const _editorMenuDisplayDetails = {
        ...editorMenuDisplayDetails,
        actionIndex: updated
      };

      return _editorMenuDisplayDetails;

    });

    event.preventDefault();
    event.stopPropagation();

  }

  function tryHandleUpArrowKeyEventForSlashMenu(event) {

    if (editorMenuDisplayDetails.display === false) {
      return;
    }

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails(editorMenuDisplayDetails) {

      let updated = editorMenuDisplayDetails.actionIndex - 1;

      const _editorMenuDisplayDetails = {
        ...editorMenuDisplayDetails,
        actionIndex: updated
      };

      return _editorMenuDisplayDetails;
    });

    event.preventDefault();
    event.stopPropagation();

  }

  function tryHandleEscapeKeyEventForSlashMenu(event) {

    if (editorMenuDisplayDetails.display === false) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    hideEditorMenu();

  }

  function handleEnterKeyEventForSlashMenu(event) {

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails(editorMenuDisplayDetails) {
      return {
        ...editorMenuDisplayDetails,
        applySelectedAction: true
      };
    });

    event.preventDefault();
    event.stopPropagation();

  }

  function handleEnterKeyEvent(event) {

    if (typeof props.onEnterKey === 'function' && event.shiftKey === false) {
      const result = props.onEnterKey(event, editor);
      if (result === true) {
        return;
      }
    }

    if (editorMenuDisplayDetails.display === true) {
      handleEnterKeyEventForSlashMenu(event);
      return;
    }
    const processed = HelptapEditor.processEnterKey(editor);
    console.log({ processed });
    if (processed === true) {

      event.preventDefault();
      return;
    }

    HelptapEditor.insertNextBlock(editor, event);

  }

  function handleDownArrowKeyEvent(event) {

    tryHandleDownArrowKeyEventForSlashMenu(event);

  }

  function handleUpArrowKeyEvent(event) {

    tryHandleUpArrowKeyEventForSlashMenu(event);

  }

  function handleEscapeKeyEvent(event) {

    tryHandleEscapeKeyEventForSlashMenu(event);

  }

  function trySetSearchTermEndForSlashMenu() {

    setEditorMenuDisplayDetails(function updateEditorMenuDisplayDetails(editorMenuDisplayDetails) {

      const { start, end } = editorMenuDisplayDetails;

      if (start === null) {
        return editorMenuDisplayDetails;
      }

      if (end === null) {
        return editorMenuDisplayDetails;
      }

      if (Node.has(editor, end.path) === false || Text.isText(Node.get(editor, end.path)) === false) {
        return editorMenuDisplayDetails;
      }

      const [leaf] = Editor.leaf(editor, end);
      const text = leaf.text.substring(start.offset + 1, end.offset + 1);

      return {
        ...editorMenuDisplayDetails,
        searchTerm: text
      };
    });

  }

  function determineDirection() {
    return props.direction || 'ltr';
  }

  function handleEditorContentChange(_value) {

    // Shallow compare works in this case.
    // So, if old value & new value is same, let's not do anything!
    if (props.value === _value) {
      return;
    }

    // setValue(_value);
    trySetSearchTermEndForSlashMenu();
    HelptapEditor.lastUpdated.update(editor);
    HelptapEditor.pageAuthor.update(editor);

    debouncedTryCallOnChange(_value);

  }

  function tryHandleMentionTriggerKey(event) {

    const previousCharacter = HelptapEditor.getPreviousCharacter(editor);

    if (previousCharacter !== ' ' && editor.selection.focus.offset > 1) {
      return;
    }

    event.preventDefault();

    HelptapEditor.mention.insertNode(editor);

  }

  function isEditorEmpty(editor) {

    if (HelptapEditor.isEmpty(editor) === true) {
      return true;
    }

    if (props.commandMenuOnByDefault === true && Node.string(editor) === '/') {
      return true;
    }

    return false;

  }

  function handleKeyEvent(event, editor) {

    const key = event.key;
    const shiftKey = event.shiftKey;

    if (typeof props.onKeyDown === 'function') {
      props.onKeyDown(event, isEditorEmpty(editor));
    }

    const commandKeyPressed = isCommandKeyPressed(event);
    tryFilterEditorMenuItems();
    // hideEditorMenu();
    if (key === '/') {
      tryShowEditorMenu();
    } else if (key === '`' && commandKeyPressed === true) {
      event.preventDefault();
      convertToOrCreateCodeBlock(editor);
    } else if (key === 'b' && commandKeyPressed === true) {
      event.preventDefault();
      HelptapEditor.toggleBoldMark(editor);
    } else if (key === 'i' && commandKeyPressed === true) {
      event.preventDefault();
      HelptapEditor.toggleItalicMark(editor);
    } else if (key === 'u' && commandKeyPressed === true) {
      event.preventDefault();
      HelptapEditor.toggleUnderlineMark(editor);
    } else if ((key === 'c' || key === 'C') && commandKeyPressed === true && shiftKey === true) {
      const copied = HelptapEditor.copyPaste.copyBlockStyles(editor);
      if (copied === true) {
        event.preventDefault();
      }
    } else if ((key === 'v' || key === 'V') && commandKeyPressed === true && shiftKey === true) {
      const pasted = HelptapEditor.copyPaste.pasteBlockStyles(editor);
      if (pasted === true) {
        event.preventDefault();
      }
    } else if (key === 'c' && commandKeyPressed === true) {
      if (editor.selection !== null && Range.isCollapsed(editor.selection) === true) {
        event.preventDefault();
        HelptapEditor.copyPaste.copySelectionToClipboard(editor);
      }
    } else if (key === 's' && commandKeyPressed === true && shiftKey === true) {
      event.preventDefault();
      HelptapEditor.toggleStrikeThroughMark(editor);
    } else if (key === ' ') {
      HelptapEditor.handleSpaceKey(editor, event, props.disabledElements || []);
    } else if (key === '-') {
      const processed = HelptapEditor.tryAddMdash(editor, event);
      if (processed === true) {
        event.preventDefault();
      }
    } else if (key === 'Enter' && (props.reserveEnterKey === true || shiftKey === false)) {

      handleEnterKeyEvent(event);

    } else if (key === 'Enter' && shiftKey === true) {

      event.preventDefault();
      Transforms.insertNodes(editor, [{ text: '\n' }]);

    } else if (key === 'Backspace') {
      if (event.altKey) {
        editor.deleteBackwardMode = 'word';
      } else if (event.metaKey || event.ctrlKey) {
        editor.deleteBackwardMode = 'line';
      } else {
        editor.deleteBackwardMode = 'character';
      }
      const previousCharacter = HelptapEditor.getPreviousCharacter(editor);
      if (previousCharacter === '/' && editor.selection.focus.offset === 1) {
        hideEditorMenu();
      }

    } else if (key === '@') {

      tryHandleMentionTriggerKey(event);

    } else if (key === 'ArrowDown') {

      handleDownArrowKeyEvent(event);

    } else if (key === 'ArrowUp') {

      handleUpArrowKeyEvent(event);

    } else if (key === 'Escape') {

      handleEscapeKeyEvent(event);

    } else if (key === 'Tab') {

      let result = false;

      if (shiftKey === true) {
        result = HelptapEditor.handleShiftTabKeyEvent(event, editor);
      } else {
        result = HelptapEditor.handleTabKeyEvent(event, editor);
      }

      event.preventDefault();

    }

  }

  function getFontFamiliesReferences(content) {

    const fontFamilies = basicEditorValueSerializer.scanners.scanFontFamilies(content);

    const fontFamiliesReferences = {};

    for (let fontFamily of fontFamilies) {

      fontFamiliesReferences[fontFamily] = 1;

    }

    return fontFamiliesReferences;

  }

  function tryLoadFontFamilies(content) {

    const fontFamiliesReferences = getFontFamiliesReferences(content);

    application.externalFontFamiliesManager.activateFontFamilies(fontFamiliesReferences);

  }

  function toBlockPath(path) {

    const node = Node.get(editor, path);

    if (Text.isText(node)) {
      return path.slice(0, -1);
    }

    return path;

  }

  function buildBlockLevelSelectionAddress(selectionAddress) {

    if (selectionAddress === null) {
      return null;
    }

    const anchorBlockPath = toBlockPath(selectionAddress.anchor.path);
    const focusBlockPath = toBlockPath(selectionAddress.focus.path);

    if (JSON.stringify(anchorBlockPath) === JSON.stringify(focusBlockPath)) {
      return { path: anchorBlockPath };
    }

    const commonAncestor = [];

    for (let i = 0; i < Math.min(anchorBlockPath.length, focusBlockPath.length); i++) {
      if (anchorBlockPath[i] !== focusBlockPath[i]) {
        break;
      }
      commonAncestor.push(anchorBlockPath[i]);
    }

    const anchorChildIndex = anchorBlockPath[commonAncestor.length];
    const focusChildIndex = focusBlockPath[commonAncestor.length];
    const startIndex = Math.min(anchorChildIndex, focusChildIndex);
    const endIndex = Math.max(anchorChildIndex, focusChildIndex) + 1;

    return {
      start: commonAncestor.concat([startIndex]),
      end: commonAncestor.concat([endIndex])
    };

  }

  function getSelectedNodes(blockSelectionAddress) {

    if (blockSelectionAddress === null) {
      return null;
    }

    if (blockSelectionAddress.path !== undefined) {
      return [Node.get(editor, blockSelectionAddress.path)];
    }

    const start = blockSelectionAddress.start;
    const end = blockSelectionAddress.end;
    const parentPath = start.slice(0, -1);
    const startIndex = start[start.length - 1];
    const endIndex = end[end.length - 1];
    const nodes = [];

    for (let i = startIndex; i < endIndex; i++) {
      nodes.push(Node.get(editor, parentPath.concat([i])));
    }

    return nodes;

  }

  function emitSelectionChange(nodes, slateSelection, blockSelectionAddress) {

    if (typeof props.onSelectChange !== 'function') {
      return;
    }

    const signature = JSON.stringify({
      slateSelection,
      blockSelectionAddress
    });

    if (signature === previousSelectionSignatureRef.current) {
      return;
    }

    previousSelectionSignatureRef.current = signature;

    props.onSelectChange(nodes, slateSelection, blockSelectionAddress);

  }

  function tryCallOnSelectChange(blockPath) {

    if (enableAdvancedOptions === true) {
      const stores = ensureEditorStores(editor);
      stores.selectedBlockKeys.set(HelptapEditor.getDirectlySelectedBlockKeys(editor));
    }

    if (blockPath !== undefined) {
      const blockSelectionAddress = { path: blockPath };
      const nodes = getSelectedNodes(blockSelectionAddress);
      emitSelectionChange(nodes, null, blockSelectionAddress);
      return;
    }

    const slateSelection = editor.selection;
    const blockSelectionAddress = buildBlockLevelSelectionAddress(slateSelection);
    const nodes = getSelectedNodes(blockSelectionAddress);

    emitSelectionChange(nodes, slateSelection, blockSelectionAddress);

  }

  selectionEmitterRef.current = tryCallOnSelectChange;

  function tryCallOnChange(value) {

    if (hasSpinner(value) === true) {
      return;
    }

    if (props.readOnly === true) {
      return;
    }

    tryLoadFontFamilies(value);

    props.onChange(value);

  }

  function _renderEditorLeaf(props) {

    return <Leaf {...props} />;

  }

  function preventTripleClickWhenToolbarOpen(event) {

    if (event.detail < 3) {
      return;
    }

    const selection = editor.selection;

    if (selection === null) {
      return;
    }

    if (Range.isCollapsed(selection) === true) {
      return;
    }

    event.preventDefault();

  }

  function _decorateEditorRanges(nodeEntry) {

    const node = nodeEntry[0];
    const path = nodeEntry[1];
    const ranges = [];

    if (aiSelectionHighlightRange === null) {
      return ranges;
    }

    if (Text.isText(node) === false) {
      return ranges;
    }

    const nodeRange = Editor.range(editor, path);
    const intersection = Range.intersection(aiSelectionHighlightRange, nodeRange);

    if (intersection === null) {
      return ranges;
    }

    ranges.push({
      anchor: intersection.anchor,
      focus: intersection.focus,
      aiSelectionHighlight: true
    });

    return ranges;

  }

  function _renderEditorElement(_props) {

    const properties = {
      ..._props,
      placeholder: false,
      enableAdvancedOptions,
      enabledDragAndDropOnly,
      activeElementPath: rootState.activeNodePathReference,
      activeHandle: rootState.activeHandle,
      editorMainReference,
      toggleEditorMenu,
      mediaUploader: props.mediaUploader,
      parentEntityId: props.parentEntityId,
      parentEntity: props.parentEntity,
      context: {
        entity: props.entity
      },
      setReadOnly,
      nodeRemover: props.nodeRemover,
      dragMenuOnByDefault: props.dragMenuOnByDefault,
      onSelectChange: tryCallOnSelectChange
    };

    if (enableAdvancedOptions === true) {
      properties.placeholder = props.placeholder;
    }

    if (properties.attributes) {
      properties.attributes = {
        ...properties.attributes,
        dir: determineDirection()
      };
    } else {
      properties.attributes = {
        dir: determineDirection()
      };
    }

    return <Element {...properties} />;

  }

  function renderToolbar() {

    if (props.overlayMenu === false) {
      return;
    }

    const toolbarProperties = {
      toolbarCount,
      editorMainReference,
      viewportReference,
      displayURLInputBox: HelptapEditor.isLinkActive(editor),
      zoom: props.zoom || 1,
      readOnly: props.readOnly,
      parentEntityId: props.parentEntityId,
      disabledElements: props.disabledElements,
      aiInstructionEnabled: props.aiInstructionEnabled === true,
      setAISelectionHighlight: setAiSelectionHighlightRange
    };

    return <Toolbar {...toolbarProperties} />;

  }

  function tryRenderMoreNodeActionsMenu() {

    const moreNodeActionsMenuDisplayDetails = rootState.moreNodeActionsMenuDisplayDetails;

    if (moreNodeActionsMenuDisplayDetails.display === false) {
      return;
    }
    if (rootState.activeNodePathReference.current === null) {
      return;
    }

    const moreNodeActionsMenuProperties = {
      ...moreNodeActionsMenuDisplayDetails,
      activeNodePathReference: rootState.activeNodePathReference,
      editor,
      parentEntityId: props.parentEntityId,
      disableAdvancedDragHandleFeatures,
      onClose() {
        // toggleMoreNodeActionsMenu();
      }
    };

    return <MoreNodeActionsMenu {...moreNodeActionsMenuProperties} />;

  }

  function tryRenderNodeMenu() {

    const nodeMenuDisplayDetails = rootState.nodeMenuDisplayDetails;

    if (nodeMenuState.isOpen === false || nodeMenuDisplayDetails.disabledNodeControls === true) {
      return;
    }

    const anchor = nodeMenuState.anchorRect;
    const nodeRoot = nodeRootByKey.get(nodeMenuState.activeKey);

    const editorActionsProperties = {
      editor,
      viewportReference,
      mediaUploader: props.mediaUploader,
      anchorRectangle: anchor,
      elementPath: nodeMenuState.getPath(),
      customNodeControls: nodeMenuState.customNodeControls,
      disabledNodeControls: nodeMenuState.disabledNodeControls,
      hoverControlsEnabled: props.hoverControlsEnabled,
      nodeRoot,
      parentEntityId: props.parentEntityId,
      disableAdvancedDragHandleFeatures,
      onClose() {
        _closeNodeMenu();
      }
    };

    const moreNodeActionsMenuProperties = {
      activeNodePath: nodeMenuState.getPath(),
      editor,
      parentEntityId: props.parentEntityId,
      disableAdvancedDragHandleFeatures,
      mediaUploader: props.mediaUploader,
      onClose() {
        _closeNodeMenu();
      }
    };

    const dragHandleMenuProperties = {
      enableAdvancedOptions,
      stylingMenu: editorActionsProperties,
      settingsMenu: moreNodeActionsMenuProperties,
      onClose() {
        _closeNodeMenu();
      }
    };

    return <DragHandleMenu {...dragHandleMenuProperties} />;

  }

  function tryRenderEditorMenu() {

    if (editorMenuDisplayDetails.display === false) {
      return;
    }

    const editorActionsProperties = {
      editor,
      viewportReference,
      mediaUploader: props.mediaUploader,
      anchorRectangle: editorMenuDisplayDetails.anchorRectangle,
      caller: editorMenuDisplayDetails.caller,
      searchTerm: editorMenuDisplayDetails.searchTerm,
      disabledElements: props.disabledElements || [],
      enabledElements: props.enabledElements || [],
      insertPoint: editorMenuDisplayDetails.start,
      actionIndex: editorMenuDisplayDetails.actionIndex,
      applySelectedAction: editorMenuDisplayDetails.applySelectedAction,
      parentEntityId: props.parentEntityId,
      parentEntityType: props.parentEntityType,
      autoFocus: false,
      level: props.level || 0,
      displayLayouts: props.displayLayouts,
      displayFavouriteBlocks: props.displayFavouriteBlocks,
      onClose() {
        hideEditorMenu();
      }
    };

    return <EditorActions {...editorActionsProperties} />;

  }

  function renderMain() {

    const editorMainAttributes = {
      className: `basic-text-editor-main hide-scrollbar ${determineDirection()}`,
      ref: editorMainReference,
      onClick(event) {

        if (event.target !== editorMainReference.current) {
          return;
        }

        HelptapEditor.tryInsertParagraphInTheEnd(editor);

        HelptapEditor.tryFocusV2(editor);

      },
      onDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
      },
      onDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof props.onFilesDrop === 'function') {
          props.onFilesDrop(event.dataTransfer.files);
        } else {
          HelptapEditor.file.handleDrop(editor, event);
        }
        return false;
      }
    };

    const slateProperties = {
      editor,
      initialValue: basicEditorValueSerializer.normalizers.normalize(props.value),
      tabIndex: props.tabIndex,
      onChange: function handler(next) {
        setValue(next); // <-- let Slate own its state
        trackGalleryResources(editor);
        trackCarouselResources(editor);
        editorChangeHandler(next); // your existing logic (debounced onChange etc.)
        tryCallOnSelectChange();
      },
      onSelectionChange: function handler() {
        tryCallOnSelectChange();
      }
    };

    const editableProperties = {
      readOnly: rootState.readOnly,
      renderElement: renderEditorElement,
      renderLeaf: renderEditorLeaf,
      decorate: decorateEditorRanges,
      tabIndex: props.tabIndex,
      onKeyDown(event) {
        return handleKeyEvent(event, editor);
      },
      onMouseDown(event) {
        preventTripleClickWhenToolbarOpen(event);
      },
      onFocus(...parameters) {
        tryCallOnFocusCallabck(...parameters);
      },
      onBlur(...parameters) {
        tryCallOnBlurCallabck(...parameters);
      },
      onCopy(event) {
        HelptapEditor.copyPaste.copySelection(event, editor);
      },
      onPaste(event) {
        const handled = HelptapEditor.copyPaste.pasteClipboardContent(event, editor);
        if (handled === true) {
          event.preventDefault();
        }
      },
      onDragStart(event) {
        return true;
      },
      onDrop(event) {
        event.stopPropagation();
        event.preventDefault();
        if (typeof props.onFilesDrop === 'function') {
          props.onFilesDrop(event.dataTransfer.files);
        } else {
          HelptapEditor.file.handleDrop(editor, event);
        }
        return true;
      },
      onDragOver(event) {
        return true;
      }
    };

    if (rootState.readOnly !== true) {
      editableProperties.placeholder = props.placeholder;
    }

    if (props.placeholderOnEveryBlock === true) {
      editableProperties.renderPlaceholder = () => null;
    }

    return <div {...editorMainAttributes}>

      <Slate {...slateProperties}>
        {renderToolbar()}
        <Editable  {...editableProperties} />
        {tryRenderNodeMenu()}
      </Slate>

      <div className="editor-dynamic-components" contentEditable={false}>
        {tryRenderEditorMenu()}
      </div>

    </div>;

  }

  return renderMain();

});

BasicTextEditor.findNodeByType = HelptapEditor.element.findNodeByType;

export default BasicTextEditor;

export function render(mountPoint, properties) {

  let root = ReactDOM.createRoot(mountPoint);
  let editorRef = { current: null };

  function buildProperties(props) {
    const originalOnEditorReady = props.onEditorReady;
    return {
      ...props,
      onEditorReady: function onEditorReady(editor) {
        editorRef.current = editor;
        if (typeof originalOnEditorReady === 'function') {
          originalOnEditorReady(editor);
        }
      }
    };
  }

  root.render(<BasicTextEditor {...buildProperties(properties)} />);

  return {
    update(properties) {

      if (root === null) {
        return;
      }

      root.render(<BasicTextEditor {...buildProperties(properties)} />);
    },
    unmount() {

      if (root === null) {
        return;
      }

      root.unmount();

      root = null;
    },
    remount(properties) {

      if (root) {
        try {
          root.unmount();
        } catch (exception) {}
      }

      root = ReactDOM.createRoot(mountPoint);

      root.render(<BasicTextEditor {...buildProperties(properties)} />);
    },
    focus() {

      if (editorRef.current === null) {
        return;
      }

      ReactEditor.focus(editorRef.current);
    }
  };

}

export class InlineNativeEditor {

  constructor(options) {

    this.el = el('.inline-native-editor-main');

    this.editor = render(this.el, {
      ...options,
      enabledElements: [
        types.DEFAULT
      ],
      displayLayouts: false,
      displayFavouriteBlocks: false
    });

  }

  onunmount() {
    this.editor.unmount();
    console.log('Unmounted editor');
  }

}
